// Browser client for /api/ai/say (non-streaming) and /api/ai/personality (one-shot).
// Reasoning models (deepseek-v4-flash) leak reasoning tokens in stream mode,
// so we use non-streaming and let the worker return the final text only.

import { type Personality, type PersonalityId, getPersonality } from './personalities';

const API_BASE = '';

export interface SayContext {
  event: 'opening' | 'kill' | 'death' | 'ally_low' | 'idle' | 'player_radio';
  selfPersonality: PersonalityId;
  selfBackstory?: { name: string; mood: string; grudges: string; catchphrases: string[] };
  recentDialogue?: Array<{ name: string; text: string }>;
  playerMessage?: string;
  killVictimName?: string;
  enemyOrAlly?: 'ally' | 'enemy';
}

export interface SayOptions {
  signal?: AbortSignal;
  hardTimeoutMs?: number;
}

const fmtRecent = (msgs?: Array<{ name: string; text: string }>) =>
  !msgs || msgs.length === 0 ? '无' : msgs.map((m) => `${m.name}: ${m.text}`).join(' / ');

function buildUserPrompt(ctx: SayContext, p: Personality): string {
  const back = ctx.selfBackstory ?? p.fallbackBackstory;
  const head = `你是${back.name}（${p.displayName}），心情：${back.mood}。最近台词：${fmtRecent(ctx.recentDialogue)}。`;
  switch (ctx.event) {
    case 'opening':
      return `${head}\n比赛刚开始，对全场说一句开场白。`;
    case 'kill':
      return `${head}\n你刚击落了 ${ctx.killVictimName ?? '敌人'}，说一句庆祝/嘲讽。`;
    case 'death':
      return `${head}\n你被击落了，说一句临死台词。`;
    case 'ally_low':
      return `${head}\n队友血量很低，对队友喊一句话（鼓励或求救）。`;
    case 'idle':
      return `${head}\n现在场上没什么事，说一句打发时间的话。`;
    case 'player_radio':
      return `${head}\n${ctx.enemyOrAlly === 'enemy' ? '敌方' : '队友'}（人类玩家）通过电台说："${ctx.playerMessage ?? ''}"。回应一句，要符合你的性格和你们的关系。`;
  }
}

export interface SayResult {
  text: string;
  ttfbMs: number;
  tokens: number;
  source: 'live' | 'fallback';
}

export async function aiSay(ctx: SayContext, opts: SayOptions = {}): Promise<SayResult> {
  const p = getPersonality(ctx.selfPersonality);
  const userPrompt = buildUserPrompt(ctx, p);

  const ctrl = new AbortController();
  const hardTimeout = setTimeout(() => ctrl.abort('hard_timeout'), opts.hardTimeoutMs ?? 4500);
  if (opts.signal) opts.signal.addEventListener('abort', () => ctrl.abort());
  const start = performance.now();

  try {
    const resp = await fetch(`${API_BASE}/api/ai/say`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: [
          { role: 'system', content: p.systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        max_tokens: 80,
        temperature: 0.95,
      }),
      signal: ctrl.signal,
    });
    if (!resp.ok) throw new Error(`http_${resp.status}`);
    const data = await resp.json() as { text?: string; ttfbMs?: number; tokens?: number; error?: string };
    const text = (data.text ?? '').trim();
    if (!text) throw new Error('empty_text');
    return { text, ttfbMs: Math.round(performance.now() - start), tokens: data.tokens ?? 0, source: 'live' };
  } catch (err) {
    const lines = p.fallbackLines;
    const pick = lines[Math.floor(Math.random() * lines.length)];
    console.warn('[aiSay] fallback used:', err instanceof Error ? err.message : err);
    return { text: pick, ttfbMs: Math.round(performance.now() - start), tokens: 0, source: 'fallback' };
  } finally {
    clearTimeout(hardTimeout);
  }
}

export interface BackstoryResult {
  name: string;
  mood: string;
  grudges: string;
  catchphrases: string[];
  source: 'live' | 'fallback';
}

export async function aiPersonality(personalityId: PersonalityId, otherPlanes: string[] = []): Promise<BackstoryResult> {
  const p = getPersonality(personalityId);
  try {
    const resp = await fetch(`${API_BASE}/api/ai/personality`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ basePersonality: p.displayName, otherPlanes }),
    });
    if (!resp.ok) throw new Error(`http_${resp.status}`);
    const j = await resp.json() as Partial<BackstoryResult>;
    if (j && typeof j === 'object' && j.name && Array.isArray(j.catchphrases) && j.catchphrases.length > 0) {
      return {
        name: j.name,
        mood: j.mood ?? p.fallbackBackstory.mood,
        grudges: j.grudges ?? p.fallbackBackstory.grudges,
        catchphrases: j.catchphrases,
        source: 'live',
      };
    }
    throw new Error('invalid_shape');
  } catch (err) {
    console.warn('[aiPersonality] fallback used:', err instanceof Error ? err.message : err);
    return { ...p.fallbackBackstory, source: 'fallback' };
  }
}
